import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TextBoxComponent } from '@progress/kendo-angular-inputs';

import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { DEFAULT_DEBOUNCE_TIME, TABLE_VIEW_MODES } from 'src/app/extensions/constants/commonConstants';
import { AssetAPIService } from 'src/app/extensions/services/api/asset-api.service';
import { GlobalService } from 'src/app/extensions/services/global.service';
import { ScrollService } from 'src/app/extensions/services/scroll.service';
import { scrollTop } from 'src/app/extensions/utilities';

@Component({
  selector: 'assets-fleet-management-list',
  standalone: false,
  templateUrl: './assets-fleet-management-list.component.html',
  styleUrl: './assets-fleet-management-list.component.scss',
  providers: [ScrollService],
})
export class AssetsFleetManagementListComponent implements OnInit, OnDestroy {
  @ViewChild('assetSearch') public assetSearchControl: TextBoxComponent;
  public pageFilter: app.models.GetPagedAssetsPageFilterModel;
  searchQuery$ = new Subject<string>();
  public TABLE_VIEW_MODES = TABLE_VIEW_MODES;
  public tableViewMode: string;
  public currentRoute: string;
  public hasData = true;
  public activeFilterCount = 0;
  private pageIndex = 1;
  public assets: app.models.DriverAssetListModel[] = [];
  public props: app.models.DynamicPropsDictionary;
  private maxIndex: number;
  public showBackToTop: boolean = false;
  public subscriptions: Subscription[] = [];

  constructor(
    private _globalService: GlobalService,
    private _assetAPIService: AssetAPIService,
    private _scrollService: ScrollService,
    private _router: Router
  ) {}

  ngOnInit(): void {
    this.pageFilter = {
      contractIds: [],
      contractTypeCodes: [],
      searchText: '',
      activeContracts: true,
    };
    this.loadData();

    let backToTopSubscription = this._scrollService.obsShowBackToTop.subscribe(x => {
      this.showBackToTop = x;
    });
    this.subscriptions.push(backToTopSubscription);

    let scrollSubscription = this._scrollService.obsFetchData.subscribe(() => {
      if (this.pageIndex <= this.maxIndex) {
        this.pageIndex++;
        this._assetAPIService.getAssets(this.pageIndex, this.pageFilter).subscribe(x => {
          this.assets = this.assets.concat(x.data.values);

          if (this.pageIndex <= this.maxIndex) {
            this._scrollService.waitingForDataFetch = false;
          }
        });
      }
    });
    this.subscriptions.push(scrollSubscription);

    let searchSubscription = this.searchQuery$
      .pipe(debounceTime(DEFAULT_DEBOUNCE_TIME), distinctUntilChanged())
      .subscribe(res => {
        console.log(res);

        this.pageIndex = 1;
        this.assetSearchControl.blur();
        this.pageFilter.searchText = res;
        this.loadData(true);
      });
    this.subscriptions.push(searchSubscription);
  }

  ngOnDestroy(): void {
    if (this.subscriptions) {
      this.subscriptions.forEach(subscription => {
        subscription.unsubscribe();
      });
    }
  }

  public toggleViewMode(tableViewMode: string) {
    this.tableViewMode = tableViewMode;
    this._globalService.toggleTableViewMode(this.currentRoute, tableViewMode);
  }

  resetFiltersHandler() {
    this.pageFilter = {
      contractIds: [],
      contractTypeCodes: [],
      searchText: '',
      activeContracts: true,
    };

    this.activeFilterCount = 0;
    this.pageIndex = 1;
    this.loadData();
  }

  loadData(forcedFocusSearchBar: boolean = false) {
    this._assetAPIService.getAssets(this.pageIndex, this.pageFilter).subscribe(x => {
      this.assets = x.data.values;
      //mock
      this.assets = this.assets.map((x, i) => ({
        ...x,
        mileage: 120000 + i * 5000,
        contractItemServices: x.contractItemServices,
        warranty: x.warrantyDate,
        driverClass: i % 2 ? 'A' : 'B',
        issuesNumber: i % 2 ? 3 : 0,
        status: !x.driverName ? 'Unassigned' : i === 2 ? 'In Workshop' : 'Assigned',
        fuelConsumtions: (10 + i).toString(),
      }));
      //
      this.props = x.props;
      this.maxIndex = x.data.totalPages;
      this.hasData = x.data.totalCount > 0;

      this._scrollService.waitingForDataFetch = this.pageIndex > this.maxIndex;

      if (forcedFocusSearchBar) {
        this.assetSearchControl.focus();
      }
    });
  }

  scrollToTop(): void {
    scrollTop();
  }

  navigateToDetailHandler(assetId: string) {
    const selectedAsset = this.assets.find(x => x.assetId === assetId);
    console.log(selectedAsset);
    localStorage.setItem(
      'SELECTED_ASSET',
      JSON.stringify({ mileage: selectedAsset.mileage, driverClass: selectedAsset.driverClass })
    );
    this._router.navigate(['/asset-fleet-management-list', assetId], {
      queryParams: { returnUrl: this._router.url.split('?')[0] },
    });
  }
}
